import type { SxProps, Theme } from "@mui/material";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { gridClasses, useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { ConditionConfiguration } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import DataGrid from "@entrilia/shared/components/grid/DataGrid.tsx";
import IntercomLauncherDependantElement from "@entrilia/shared/components/IntercomLauncherDependantElement.tsx";
import useMergedSx from "@entrilia/shared/hooks/useMergedSx.ts";
import type {
  FinancialStatementGeneralSettings,
  FinancialStatementResponse,
  StatementDataColumnConfiguration,
  StatementLineConfiguration,
  StatementValueCell,
} from "../../../../../../../api/types/svTypes.ts";
import useGridColumnWidths from "../../../hooks/useGridColumnWidths.ts";
import useResizeObserver from "../../../hooks/resizeObserver.ts";
import { selectPreviewMode } from "../../../store/selectors/currentReportSelectors.ts";
import { PreviewDefaultSize } from "../../../utilities/const.ts";
import { getBiDataGridStyles } from "../../../utilities/Utilities.tsx";
import { isValueLine } from "../../builder/financialStatement/utils/statementLines.ts";
import ReportLoadingOverlay from "../../common/ReportLoadingOverlay.tsx";
import PivotExpansionButton from "../pivot/PivotExpansionButton.tsx";
import {
  buildStatementColumns,
  buildStatementRows,
  type CellPeriod,
  type GetFieldCaption,
  getCellPeriod,
  getCellPeriodDescription,
  isRedNegative,
  LABEL_COLUMN_FIELD,
  type LinePeriod,
  periodCode,
  resolveStatementLinePeriods,
  type StatementColumnHeader,
  type StatementColumnPeriod,
  type StatementGridColumn,
  type StatementGridRow,
  TOTAL_COLUMN_CLASS,
} from "./statementGridModel.ts";

interface Props {
  response: FinancialStatementResponse | undefined;
  lines: StatementLineConfiguration[];
  dataColumns: StatementDataColumnConfiguration[];
  conditions: ConditionConfiguration[];
  settings: FinancialStatementGeneralSettings;
  getFieldCaption: GetFieldCaption;
  loading: boolean;
  selectedLineId?: string;
  onLineClick?: (lineId: string) => void;
}

interface LabelExpansion {
  expanded: boolean;
  onToggle: () => void;
}

interface GridColumnsOptions {
  columns: StatementGridColumn[];
  lines: StatementLineConfiguration[];
  settings: FinancialStatementGeneralSettings;
  linePeriods: ReadonlyMap<string, LinePeriod>;
  labelExpansion: LabelExpansion | undefined;
  columnWidths: ReadonlyMap<string, number>;
  onToggleLine: (lineId: string) => void;
}

const LABEL_INDENT_PX = 12;
const LEVEL_INDENT_PX = 16;
const TOGGLE_SLOT_PX = 30;
const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 32;
const EMPHASIS_ROW_CLASS = "statement-row--emphasis";
const TOTAL_ROW_CLASS = "statement-row--total";
const GRAND_TOTAL_ROW_CLASS = "statement-row--grand-total";

const noCollapsedLines: ReadonlySet<string> = new Set();
const gridSlots = { loadingOverlay: ReportLoadingOverlay };
const dataGridContainerSx: SxProps<Theme> = { alignItems: "center" };

const gridBoxSx: SxProps<Theme> = (theme) => ({
  display: "flex",
  flex: 1,
  alignItems: "center",
  position: "relative",
  fontFamily: theme.typography.h1.fontFamily,
});

const statementGridSx: SxProps<Theme> = (theme) => ({
  [`& .${gridClasses.columnHeaderTitleContainerContent}`]: { flex: 1 },
  [`& .${gridClasses.columnHeader}.${TOTAL_COLUMN_CLASS}`]: { backgroundColor: theme.palette.background.grey },
  [`& .${gridClasses.row}:not(.Mui-selected) .${TOTAL_COLUMN_CLASS}`]: {
    backgroundColor: theme.palette.background.grey,
  },
  [`& .${EMPHASIS_ROW_CLASS}`]: { fontWeight: theme.typography.fontWeightMedium },
  [`& .${TOTAL_ROW_CLASS}:not(.Mui-selected) .${gridClasses.cell}`]: {
    backgroundColor: theme.palette.background.grey,
  },
  [`& .${GRAND_TOTAL_ROW_CLASS} .${gridClasses.cell}`]: {
    borderBottom: `3px double ${theme.palette.text.primary}`,
  },
});

const isEveryLineCollapsed = (lineIds: string[], collapsedLineIds: ReadonlySet<string>): boolean =>
  lineIds.length > 0 && lineIds.every((lineId) => collapsedLineIds.has(lineId));

const getRowClassName = ({ row }: GridRowClassNameParams<StatementGridRow>): string => {
  const classNames: string[] = [];
  const isTotal = row.variant === "subtotal" || row.variant === "total";
  if (isTotal || row.variant === "heading") {
    classNames.push(EMPHASIS_ROW_CLASS);
  }
  if (isTotal) {
    classNames.push(TOTAL_ROW_CLASS);
  }
  if (row.isGrandTotal) {
    classNames.push(GRAND_TOTAL_ROW_CLASS);
  }
  return classNames.join(" ");
};

const StatementEmptyState = () => (
  <Stack spacing={1} sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
    <Typography variant="subtitle1">Nothing to preview yet</Typography>
    <Typography color="textSecondary">Add a line in Rows to start building the statement.</Typography>
  </Stack>
);

interface LabelCellProps {
  row: StatementGridRow;
  hasToggleSlot: boolean;
  onToggleLine: (lineId: string) => void;
}

const StatementLabelCell = ({ row, hasToggleSlot, onToggleLine }: LabelCellProps) => (
  <Stack
    direction="row"
    sx={{ alignItems: "center", minWidth: 0, pl: `${LABEL_INDENT_PX + row.level * LEVEL_INDENT_PX}px` }}
  >
    {hasToggleSlot && (
      <Box sx={{ width: TOGGLE_SLOT_PX, flexShrink: 0 }}>
        {row.isToggle && (
          <PivotExpansionButton
            expanded={!row.isCollapsed}
            onToggle={() => onToggleLine(row.lineId)}
            label={row.isCollapsed ? "Expand row" : "Collapse row"}
          />
        )}
      </Box>
    )}
    <Typography variant="inherit" noWrap>
      {row.label}
    </Typography>
  </Stack>
);

interface PeriodCodeProps {
  code: string;
  period: CellPeriod;
}

const StatementPeriodCode = ({ code, period }: PeriodCodeProps) => (
  <Tooltip title={getCellPeriodDescription(period)} placement="top">
    <Stack direction="row" spacing={0.25} sx={{ alignItems: "flex-start", cursor: "help" }}>
      <Typography variant="caption" color="textDisabled">
        {code}
      </Typography>
      {period.source.kind === "column" && period.source.overriddenLineRange && (
        <Box sx={{ width: 4, height: 4, mt: 0.25, borderRadius: "50%", bgcolor: "warning.main", flexShrink: 0 }} />
      )}
    </Stack>
  </Tooltip>
);

interface AmountCellProps {
  cell: StatementValueCell | undefined;
  lineId: string;
  lines: StatementLineConfiguration[];
  columnPeriod: StatementColumnPeriod | undefined;
  linePeriod: LinePeriod | undefined;
  settings: FinancialStatementGeneralSettings;
}

const StatementAmountCell = ({ cell, lineId, lines, columnPeriod, linePeriod, settings }: AmountCellProps) => {
  if (!cell) {
    return null;
  }
  const code = periodCode(cell, settings);
  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ width: "100%", alignItems: "baseline" }}>
      {code && cell.dateRange && (
        <StatementPeriodCode
          code={code}
          period={getCellPeriod(cell.dateRange, linePeriod, columnPeriod, lines, lineId)}
        />
      )}
      <Typography
        variant="inherit"
        noWrap
        color={isRedNegative(cell, settings) ? "error" : undefined}
        sx={{ ml: "auto" }}
      >
        {cell.formattedValue}
      </Typography>
    </Stack>
  );
};

const StatementColumnTitle = ({ header }: { header: StatementColumnHeader }) => (
  <Stack direction="row" spacing={1} useFlexGap sx={{ width: "100%", alignItems: "center" }}>
    {header.periodCode && (
      <Typography variant="caption" color="textDisabled">
        {header.periodCode}
      </Typography>
    )}
    <Tooltip title={header.tooltip ?? ""} placement="bottom">
      <Typography variant="caption" color="textSecondary" noWrap sx={{ ml: "auto" }}>
        {header.label}
      </Typography>
    </Tooltip>
  </Stack>
);

const getGridColumns = ({
  columns,
  lines,
  settings,
  linePeriods,
  labelExpansion,
  columnWidths,
  onToggleLine,
}: GridColumnsOptions): GridColDef<StatementGridRow>[] =>
  columns.map((column): GridColDef<StatementGridRow> => {
    if (column.field === LABEL_COLUMN_FIELD) {
      return {
        ...column,
        width: columnWidths.get(column.field) ?? column.width,
        renderHeader: labelExpansion
          ? () => (
              <Box sx={{ pl: `${LABEL_INDENT_PX}px` }}>
                <PivotExpansionButton
                  all
                  expanded={labelExpansion.expanded}
                  onToggle={labelExpansion.onToggle}
                  label={labelExpansion.expanded ? "Collapse all rows" : "Expand all rows"}
                />
              </Box>
            )
          : undefined,
        renderCell: ({ row }: GridRenderCellParams<StatementGridRow>) => (
          <StatementLabelCell row={row} hasToggleSlot={labelExpansion !== undefined} onToggleLine={onToggleLine} />
        ),
      };
    }
    const { header, columnPeriod } = column;
    return {
      ...column,
      width: columnWidths.get(column.field) ?? column.width,
      renderHeader: header ? () => <StatementColumnTitle header={header} /> : undefined,
      renderCell: ({ row }: GridRenderCellParams<StatementGridRow>) => (
        <StatementAmountCell
          cell={row.cells[column.field]}
          lineId={row.lineId}
          lines={lines}
          columnPeriod={columnPeriod}
          linePeriod={linePeriods.get(row.lineId)}
          settings={settings}
        />
      ),
    };
  });

const FinancialStatementGrid = ({
  response,
  lines,
  dataColumns,
  conditions,
  settings,
  getFieldCaption,
  loading,
  selectedLineId,
  onLineClick,
}: Props) => {
  const previewMode = useSelector(selectPreviewMode);
  const [collapsedLineIds, setCollapsedLineIds] = useState<ReadonlySet<string>>(() => new Set());
  const gridBoxRef = useRef<HTMLDivElement>(null);
  const apiRef = useGridApiRef();
  const { columnWidths, handleColumnWidthChange } = useGridColumnWidths();
  const size = useResizeObserver(gridBoxRef, undefined, previewMode === "maximize" ? undefined : PreviewDefaultSize);

  const linePeriods = useMemo(
    () => resolveStatementLinePeriods(lines, settings.defaultDateRange, getFieldCaption),
    [getFieldCaption, lines, settings.defaultDateRange]
  );

  const statementColumns = useMemo(
    () =>
      response
        ? buildStatementColumns(response, dataColumns, conditions, settings, linePeriods, getFieldCaption)
        : undefined,
    [conditions, dataColumns, getFieldCaption, linePeriods, response, settings]
  );

  const rows = useMemo(
    () => (response ? buildStatementRows(response, lines, collapsedLineIds, getFieldCaption) : []),
    [collapsedLineIds, getFieldCaption, lines, response]
  );

  const toggleLineIds = useMemo(
    () =>
      response
        ? buildStatementRows(response, lines, noCollapsedLines, getFieldCaption)
            .filter((row) => row.isToggle)
            .map((row) => row.lineId)
        : [],
    [getFieldCaption, lines, response]
  );

  const handleToggleLine = useCallback((lineId: string) => {
    setCollapsedLineIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(lineId)) {
        next.add(lineId);
      }
      return next;
    });
  }, []);

  const handleToggleAllLines = useCallback(() => {
    setCollapsedLineIds((previous) =>
      isEveryLineCollapsed(toggleLineIds, previous) ? new Set() : new Set(toggleLineIds)
    );
  }, [toggleLineIds]);

  const gridColumns = useMemo(
    () =>
      getGridColumns({
        columns: statementColumns?.columns ?? [],
        lines,
        settings,
        linePeriods,
        labelExpansion:
          toggleLineIds.length > 0
            ? { expanded: !isEveryLineCollapsed(toggleLineIds, collapsedLineIds), onToggle: handleToggleAllLines }
            : undefined,
        columnWidths,
        onToggleLine: handleToggleLine,
      }),
    [
      collapsedLineIds,
      columnWidths,
      handleToggleAllLines,
      handleToggleLine,
      linePeriods,
      lines,
      settings,
      statementColumns,
      toggleLineIds,
    ]
  );

  // MUI can keep focus on a removed column while columns rebuild, which crashes its cell handlers.
  useLayoutEffect(() => {
    apiRef.current?.setState((gridState) => ({
      ...gridState,
      focus: { cell: null, columnHeader: null, columnHeaderFilter: null, columnGroupHeader: null },
    }));
  }, [apiRef, statementColumns]);

  const pinnedColumns = useMemo(() => ({ left: statementColumns?.pinnedLeft ?? [] }), [statementColumns]);

  const rowSelectionModel = useMemo(
    (): GridRowSelectionModel | undefined =>
      onLineClick
        ? { type: "include", ids: new Set(rows.filter((row) => row.lineId === selectedLineId).map((row) => row.id)) }
        : undefined,
    [onLineClick, rows, selectedLineId]
  );

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<StatementGridRow>) => {
      onLineClick?.(row.lineId);
    },
    [onLineClick]
  );

  const sx = useMergedSx(getBiDataGridStyles, { maxWidth: size.width }, statementGridSx);
  const scrollContainerSx = useMemo(() => ({ width: size.width }), [size.width]);

  if (!lines.some(isValueLine)) {
    return <StatementEmptyState />;
  }

  return (
    <Stack sx={{ flex: 1 }}>
      <IntercomLauncherDependantElement mode="opacity">
        <Box ref={gridBoxRef} sx={gridBoxSx}>
          <DataGrid<StatementGridRow>
            apiRef={apiRef}
            rows={rows}
            columns={gridColumns}
            onColumnWidthChange={handleColumnWidthChange}
            columnGroupingModel={statementColumns?.columnGroupingModel}
            pinnedColumns={pinnedColumns}
            getRowClassName={getRowClassName}
            rowSelectionModel={rowSelectionModel}
            onRowClick={onLineClick ? handleRowClick : undefined}
            selectableRows={onLineClick !== undefined}
            loading={loading}
            slots={gridSlots}
            sx={sx}
            containerSx={dataGridContainerSx}
            scrollContainerSx={scrollContainerSx}
            rowHeight={ROW_HEIGHT}
            columnHeaderHeight={HEADER_HEIGHT}
            columnGroupHeaderHeight={HEADER_HEIGHT}
            showCellVerticalBorder
            showColumnVerticalBorder
          />
        </Box>
      </IntercomLauncherDependantElement>
    </Stack>
  );
};

export default FinancialStatementGrid;
